import { useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useProfile } from "@/hooks/useProfiles"
import { useTimeline } from "@/hooks/useTimeline"
import { usePostFilters } from "@/hooks/usePostFilters"
import { useFilteredPosts } from "@/hooks/useFilteredPosts"
import { useFollowList, useFollowing, useProfileLists } from "@/hooks/useSocial"
import { useAuth } from "@/state/AuthContext"
import { useRelays } from "@/state/RelayContext"
import { useSettings } from "@/state/SettingsContext"
import { useToast } from "@/state/ToastContext"
import type { PostType } from "@/nostr/api/feed"
import { relayFilterHints } from "@/nostr/api/filters/filterEngine"
import { extractMedia } from "@/utils/content"
import { npubOf, toPubkey } from "@/utils/nip19"
import { Tabs } from "@/components/Tabs"
import { NoteCard } from "@/components/NoteCard"
import { Avatar } from "@/components/Avatar"
import { Icon } from "@/components/Icon"
import { PostFilters } from "@/components/filters/PostFilters"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { ProfileEditorDialog } from "@/components/ProfileEditorDialog"
import { NotFoundPage } from "./NotFoundPage"

type ProfileTab = "notes" | "replies" | "articles"

/** The profile tabs are a single-select post type filter (same rules as the feeds). */
const PROFILE_TAB_POST_TYPES: Record<ProfileTab, PostType[]> = {
  notes: ["note", "repost"],
  replies: ["reply"],
  articles: ["article"],
}

/**
 * Profile page: banner, avatar, npub, following/relay/mute counts, a local
 * search, notes/replies/articles tabs and the profile editor for yourself.
 */
export function ProfilePage() {
  const { t } = useTranslation()
  const params = useParams()
  const { push } = useToast()
  const { pubkey: me, canSign } = useAuth()
  const { appRelays, favoriteRelays } = useRelays()
  const pubkey = toPubkey(params.npub ?? "")
  const profile = useProfile(pubkey)
  const followList = useFollowList(pubkey)
  const following = useFollowing()
  const lists = useProfileLists(pubkey)
  const [tab, setTab] = useState<ProfileTab>("notes")
  const [search, setSearch] = useState("")
  const [editorOpen, setEditorOpen] = useState(false)
  const [bannerFailed, setBannerFailed] = useState(false)
  const { settings } = useSettings()
  const postFilters = usePostFilters("profile")
  const filters = postFilters.filters
  const hints = useMemo(() => relayFilterHints(filters), [filters])

  const isMe = Boolean(pubkey && pubkey === me)

  const timeline = useTimeline({
    relays: appRelays,
    authors: pubkey ? [pubkey] : [],
    postTypes: PROFILE_TAB_POST_TYPES[tab],
    since: hints.since,
    until: hints.until,
    enabled: Boolean(pubkey),
  })

  // The tabs own the post type dimension on this page, so the engine must not
  // apply it a second time (a note tab is "notes + reposts", which the single
  // select control cannot express).
  const engineFilters = useMemo(() => ({ ...filters, postTypes: [] }), [filters])
  const visible = useFilteredPosts(timeline.events, engineFilters, {
    mutedWords: settings.mutedWords,
  })

  const events = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return visible.events
    return visible.events.filter((event) =>
      `${event.content} ${event.tags.map((tag) => tag[1]).join(" ")}`.toLowerCase().includes(term),
    )
  }, [visible.events, search])

  const relayCount = isMe ? favoriteRelays.length : lists.relayEntries.length
  const mediaCount = useMemo(
    () => events.filter((event) => extractMedia(event.content, event.tags).length > 0).length,
    [events],
  )

  if (!pubkey) return <NotFoundPage />

  const copyNpub = async () => {
    try {
      await navigator.clipboard.writeText(npubOf(pubkey))
      push("success", "npub copied")
    } catch {
      push("error", "Could not copy the npub")
    }
  }

  return (
    <div className="page profile-page">
      <div className="page-topbar">
        <Link to="/" className="btn btn-ghost btn-small">
          <Icon name="arrow-left" size={16} /> {t("Back home")}
        </Link>
      </div>

      {profile?.banner && !bannerFailed ? (
        <div className="profile-banner">
          <img src={profile.banner} alt="" onError={() => setBannerFailed(true)} />
        </div>
      ) : (
        <div className="profile-banner profile-banner-empty" aria-hidden="true" />
      )}

      <header className="profile-header">
        <Avatar
          src={profile?.picture}
          label={profile?.displayName || pubkey}
          size={84}
          className="profile-avatar"
        />
        <div className="profile-identity">
          <h1>{profile?.displayName || profile?.name || `${pubkey.slice(0, 12)}…`}</h1>
          {profile?.nip05 && <p className="profile-nip05">{profile.nip05}</p>}
          <button type="button" className="profile-npub" onClick={copyNpub} title={t("Copy npub")}>
            <code>{npubOf(pubkey)}</code>
            <Icon name="copy" size={13} />
          </button>
        </div>
        <div className="profile-actions">
          {isMe ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setEditorOpen(true)}
              disabled={!canSign}
              title={canSign ? undefined : t("Log in with a signer to edit your profile")}
            >
              <Icon name="pencil" size={16} /> {t("Edit profile")}
            </button>
          ) : (
            <button
              type="button"
              className={`btn ${following.followingSet.has(pubkey) ? "btn-ghost" : "btn-primary"}`}
              disabled={!canSign || following.pending === pubkey}
              onClick={() => void following.toggleFollow(pubkey)}
            >
              <Icon name={following.followingSet.has(pubkey) ? "check" : "plus"} size={16} />
              {following.followingSet.has(pubkey) ? t("Unfollow") : t("Follow")}
            </button>
          )}
        </div>
      </header>

      {profile?.about && <p className="profile-about">{profile.about}</p>}

      <div className="profile-meta">
        <span title={t("People this account follows (kind 3)")}>
          <strong>{followList.following.length}</strong> {t("Following")}
        </span>
        <span title={isMe ? t("Relays in your relay library") : t("Relays in the NIP-65 relay list")}>
          <strong>{relayCount}</strong> {t("Relays")}
        </span>
        <span title={t("Muted accounts, hashtags and words (kind 10000)")}>
          <strong>{lists.mute.count}</strong> {t("Muted")}
        </span>
        {profile?.website && (
          <a href={profile.website} target="_blank" rel="noopener noreferrer" className="profile-link">
            <Icon name="link" size={14} /> {profile.website.replace(/^https?:\/\//, "")}
          </a>
        )}
        {profile?.lud16 && (
          <span className="profile-link" title={t("Lightning address")}>
            <Icon name="zap" size={14} /> {profile.lud16}
          </span>
        )}
      </div>

      <div className="relay-filter profile-filter">
        <Icon name="search" size={16} />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("Filter this profile's notes")}
          aria-label={t("Filter this profile's notes")}
        />
        {search && (
          <button
            type="button"
            className="icon-button small"
            onClick={() => setSearch("")}
            aria-label={t("Clear")}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      <Tabs
        value={tab}
        tabs={[
          { value: "notes", label: t("Notes") },
          { value: "replies", label: t("Replies") },
          { value: "articles", label: t("Articles") },
        ]}
        onChange={(value) => setTab(value as ProfileTab)}
        ariaLabel={t("Profile")}
      />

      <div className="filter-bar">
        <PostFilters
          value={filters}
          active={postFilters.active}
          onApply={(next) => postFilters.patch(next)}
          onClearAll={postFilters.clearAll}
          onRemove={postFilters.remove}
          categories={["content", "time"]}
          hidePostType
          mutedWordCount={settings.mutedWords.length}
        />
      </div>

      {visible.hidden > 0 && (
        <p className="muted small filter-hidden-note">
          {t("{n} posts hidden by the active filters", { n: visible.hidden })}
        </p>
      )}

      <div className="profile-list-header">
        <span className="muted small">
          {events.length} {tab === "articles" ? t("articles") : t("notes")} · {mediaCount} {t("with media")}
        </span>
        <button type="button" className="icon-button" onClick={timeline.refresh} aria-label={t("Refresh")}>
          <Icon name="refresh" size={16} />
        </button>
      </div>

      {timeline.loading && events.length === 0 && <Skeleton lines={4} />}

      {!timeline.loading && events.length === 0 && (
        <EmptyState
          icon={tab === "articles" ? "document" : "pen"}
          title={search ? t("No results") : t("No notes found")}
          description={
            search
              ? t("No notes on this profile match that filter.")
              : t("This profile has not published anything the connected relays know about.")
          }
          action={
            <button type="button" className="btn btn-primary" onClick={timeline.refresh}>
              {t("Reload")}
            </button>
          }
        />
      )}

      {events.map((event) => (
        <NoteCard key={event.id} event={event} onReplied={timeline.refresh} />
      ))}

      {timeline.hasMore && !search && (
        <button
          type="button"
          className="btn btn-ghost btn-block"
          onClick={timeline.loadMore}
          disabled={timeline.loadingMore}
        >
          {timeline.loadingMore ? t("Loading…") : t("Load more")}
        </button>
      )}

      {isMe && (
        <ProfileEditorDialog
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          pubkey={pubkey}
          onSaved={timeline.refresh}
        />
      )}
    </div>
  )
}
